import {useEffect, useState, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {
  Card,
  CircularProgress,
  Grid,
  Divider,
  Icon,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Box,
  Pagination,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {activityLogsApi} from 'api/activityLogsApi';
import useDebounce from 'hooks/useDebounce';
import {formatActionMessage} from 'utils/activityLogUtils';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const METHOD_OPTIONS = ['POST', 'PATCH', 'PUT', 'DELETE'];

const columns = [
  {Header: 'Time', accessor: 'time', align: 'left'},
  {Header: 'Admin', accessor: 'admin', align: 'left'},
  {Header: 'Role', accessor: 'role', align: 'center'},
  {Header: 'Action', accessor: 'action', align: 'left'},
  {Header: '', accessor: 'actions', align: 'center'},
];

const roleBadgeColor = role => (role === 'superAdmin' ? 'dark' : 'info');

const ActivityLogs = () => {
  const {user} = useSelector(state => state.auth);
  const navigate = useNavigate();
  const canRead = usePermission('activity-logs', 'read');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [adminId, setAdminId] = useState('');
  const [module, setModule] = useState('');
  const [method, setMethod] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 800);
  const debouncedAdminId = useDebounce(adminId, 800);
  const debouncedModule = useDebounce(module, 800);

  const fetchLogs = useCallback(async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {page, limit};
      if (debouncedAdminId) params.adminId = debouncedAdminId;
      if (debouncedModule) params.module = debouncedModule;
      if (method) params.method = method;
      if (from && !isNaN(new Date(from).getTime())) params.from = new Date(from).toISOString();
      if (to && !isNaN(new Date(to).getTime())) params.to = new Date(to).toISOString();
      if (debouncedSearch) params.search = debouncedSearch;

      const data = await activityLogsApi.fetchLogs(user?.accessToken, params);
      setLogs(data?.logs || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedAdminId, debouncedModule, method, from, to, debouncedSearch, user?.accessToken, canRead]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedAdminId, debouncedModule, method, from, to, debouncedSearch]);

  const getRow = row => ({
    time: (
      <Tooltip title={moment(row.createdAt).format('DD MMM YYYY, HH:mm:ss')}>
        <MDTypography variant="caption">{moment(row.createdAt).fromNow()}</MDTypography>
      </Tooltip>
    ),
    admin: (
      <MDBox display="flex" flexDirection="column">
        <MDTypography variant="button" fontWeight="medium">
          {row.adminName || '—'}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {row.adminEmail}
        </MDTypography>
      </MDBox>
    ),
    role: row.adminRole ? (
      <MDBadge badgeContent={row.adminRole} color={roleBadgeColor(row.adminRole)} variant="gradient" size="sm" />
    ) : (
      <MDTypography variant="caption">—</MDTypography>
    ),
    action: (
      <Tooltip title={formatActionMessage(row)}>
        <MDTypography
          variant="button"
          fontWeight="medium"
          sx={{
            display: 'block',
            maxWidth: 440,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {formatActionMessage(row)}
        </MDTypography>
      </Tooltip>
    ),
    actions: (
      <Tooltip title="View Details">
        <IconButton size="small" onClick={() => navigate(`/activity-logs/${row.id}`)}>
          <Icon fontSize="small">visibility</Icon>
        </IconButton>
      </Tooltip>
    ),
  });


  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to activity logs. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}>
                  <MDTypography variant="h6" color="white">
                    Activity Logs ({total})
                  </MDTypography>
                  <MDButton variant="outlined" color="white" onClick={() => fetchLogs()}>
                    <Icon sx={{mr: 0.5}}>refresh</Icon>
                    Refresh
                  </MDButton>
                </MDBox>

                <Grid container spacing={2} p={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MDInput
                      fullWidth
                      label="Search admin name/email"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      label="Admin ID"
                      placeholder="uuid"
                      value={adminId}
                      onChange={e => setAdminId(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      label="Module"
                      placeholder="e.g. products"
                      value={module}
                      onChange={e => setModule(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Method</InputLabel>
                      <Select value={method} onChange={e => setMethod(e.target.value)} label="Method">
                        <MenuItem value="">All Methods</MenuItem>
                        {METHOD_OPTIONS.map(m => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      type="date"
                      label="From"
                      InputLabelProps={{shrink: true}}
                      value={from}
                      onChange={e => setFrom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      type="date"
                      label="To"
                      InputLabelProps={{shrink: true}}
                      value={to}
                      onChange={e => setTo(e.target.value)}
                    />
                  </Grid>
                </Grid>

                {loading && (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                    <Box textAlign="center">
                      <CircularProgress size="2.5rem" color="inherit" />
                      <MDTypography variant="button" color="text" display="block" mt={1}>
                        Loading activity logs...
                      </MDTypography>
                    </Box>
                  </MDBox>
                )}

                {!loading && error && (
                  <MDBox textAlign="center" py={6} px={3}>
                    <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
                    <MDTypography variant="h6" mb={2}>
                      {error}
                    </MDTypography>
                    <MDButton variant="contained" color="info" onClick={() => fetchLogs()}>
                      Retry
                    </MDButton>
                  </MDBox>
                )}

                {!loading && !error && logs.length === 0 && (
                  <MDBox textAlign="center" py={6}>
                    <Icon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}>history</Icon>
                    <MDTypography variant="h6" mb={1}>
                      No activity recorded yet
                    </MDTypography>
                  </MDBox>
                )}

                {!loading && !error && logs.length > 0 && (
                  <>
                    <MDBox pt={3}>
                      <DataTable
                        key={logs.map(l => l.id).join('|')}
                        table={{
                          columns,
                          rows: logs.map(row => getRow(row)),
                        }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                        hoverEffect
                      />
                    </MDBox>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} py={3} px={2}>
                      <MDTypography variant="caption" color="text">
                        Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                      </MDTypography>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="info"
                        shape="rounded"
                      />
                    </MDBox>
                  </>
                )}
              </Card>
            </Grid>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default ActivityLogs;
